// Mount-time chat-persistence hydration sequence, lifted out of ChatConsole so
// the ordering, cancellation, best-effort, and startup-jitter invariants are
// unit-testable without React. The component still owns the id-fence refs and
// the tab-strip meta reload/render; this owns only the persistence orchestration.
//
// Order is load-bearing: backfill (seed PG from legacy local chats) → reconcile
// (pull the server list onto this device) → afterReconcile (the component keeps
// the id fence above any counter the reconcile advanced) → resolveConflicts
// (adopt-latest for conflict-parked chats so the drain can't re-POST a stale
// write over another device) → schedule the drain. resolveConflicts MUST run
// before the drain; the drain is fire-and-forget so landing never blocks on it.

import { chatPersistenceEnabled } from "./featureFlags.js";
import { startChatSyncBroadcast } from "./chatSyncBroadcast.js";
import { runBackfillOnce } from "./chatBackfill.js";
import { reconcileChatList } from "./chatRestore.js";
import { resolveConflicts, drainOutbox } from "./chatSyncEngine.js";

// Upper bound on the random startup delay before the outbox drain. Smooths a
// correlated mount burst (first-login rollout) so seats don't all POST in the
// same instant; it does NOT rate-limit — the fleet ceiling is the server-side
// blob-upload rate limit, which the outbox treats as a transient retry, plus the
// feature flag as the per-cohort rollout ramp.
export const DRAIN_STARTUP_JITTER_MS = 5000;

async function bestEffort(fn) {
    try { await fn(); } catch { /* best-effort — a failure leaves IDB as the truth */ }
}

// Run the flag-gated hydration sequence. Returns { ran, cancelled, drainDelay }.
// `ran` is false when the flag is off or there's no tenant (no side effects).
// Callbacks let the component inject the pieces that stay its concern:
//   allocLocalId    — advances the fence-respecting id counter for reconcile
//   afterReconcile  — component bumps the id fence above the advanced counter
//   isCancelled     — the effect-teardown flag, checked at every await boundary
//   scheduleDrain   — (fn, delayMs) => void; defaults to setTimeout
//   random          — injectable for deterministic jitter in tests
export async function runChatPersistenceHydration({
    tenantId,
    allocLocalId,
    afterReconcile = () => {},
    isCancelled = () => false,
    scheduleDrain = (fn, delayMs) => setTimeout(fn, delayMs),
    random = Math.random,
} = {}) {
    if (!chatPersistenceEnabled() || !tenantId) return { ran: false, cancelled: false, drainDelay: null };

    startChatSyncBroadcast();
    await bestEffort(() => runBackfillOnce(tenantId));
    if (isCancelled()) return { ran: true, cancelled: true, drainDelay: null };

    await bestEffort(() => reconcileChatList(tenantId, { allocLocalId }));
    if (isCancelled()) return { ran: true, cancelled: true, drainDelay: null };
    afterReconcile();

    await bestEffort(() => resolveConflicts({ tenantId }));
    if (isCancelled()) return { ran: true, cancelled: true, drainDelay: null };

    const drainDelay = Math.floor(random() * DRAIN_STARTUP_JITTER_MS);
    scheduleDrain(() => {
        if (!isCancelled()) drainOutbox({ tenantId }).catch(() => {});
    }, drainDelay);
    return { ran: true, cancelled: false, drainDelay };
}
